import type { QuickFillItem } from '../../ui/table/types';

interface DescribableColumnFilter {
  mode: 'values' | 'condition';
  operator:
    | 'contains'
    | 'notContains'
    | 'equals'
    | 'notEquals'
    | 'startsWith'
    | 'endsWith'
    | 'isEmpty'
    | 'isNotEmpty';
  value: string;
  selectedValues: string[] | null;
}

type DescribableSortDirection = 'asc' | 'desc';

export const VIRTUALIZATION_ROW_THRESHOLD = 20;
export const DEFAULT_VIEWPORT_HEIGHT = 640;
export const OVERSCAN_ROWS = 4;
export const VIEWPORT_UPDATE_INTERVAL_MS = 48;
export const ROW_HEIGHT = 40;
export const HEADER_HEIGHT = 58;
export const CHECKBOX_COLUMN_WIDTH = 52;

const NAME_COLUMN_WIDTH = 220;
const DATA_COLUMN_WIDTH = 156;
const MIN_NAME_COLUMN_WIDTH = 160;
const MIN_DATA_COLUMN_WIDTH = 120;

export interface VirtualRowRange {
  start: number;
  end: number;
}

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const resolveVirtualRowRange = ({
  rowCount,
  viewportStart,
  visibleCount,
  previousRange,
  overscanRows = OVERSCAN_ROWS,
  virtualizationThreshold = VIRTUALIZATION_ROW_THRESHOLD,
}: {
  rowCount: number;
  viewportStart: number;
  visibleCount: number;
  previousRange: VirtualRowRange;
  overscanRows?: number;
  virtualizationThreshold?: number;
}): VirtualRowRange => {
  if (rowCount <= 0) {
    return { start: 0, end: 0 };
  }

  const maxStart = Math.max(rowCount - 1, 0);
  if (rowCount <= virtualizationThreshold) {
    return { start: 0, end: maxStart };
  }

  const safeVisibleCount = Math.max(1, visibleCount);
  const safeViewportStart = clamp(viewportStart, 0, maxStart);
  const viewportEnd = clamp(safeViewportStart + safeVisibleCount - 1, 0, maxStart);
  const previousHasRows = previousRange.end >= previousRange.start;
  const previousCoversViewport =
    previousHasRows &&
    safeViewportStart >= previousRange.start &&
    viewportEnd <= previousRange.end;

  if (previousCoversViewport) {
    return previousRange;
  }

  return {
    start: clamp(safeViewportStart - overscanRows, 0, maxStart),
    end: clamp(viewportEnd + overscanRows, 0, maxStart),
  };
};

export const getEventClientX = (event: { clientX?: number }): number =>
  typeof event.clientX === 'number' && Number.isFinite(event.clientX) ? event.clientX : 0;

const describeFilter = (filter: DescribableColumnFilter | undefined): string | null => {
  if (!filter) {
    return null;
  }

  if (filter.mode === 'values') {
    return filter.selectedValues === null
      ? null
      : `${filter.selectedValues.length} selected`;
  }

  const trimmedValue = filter.value.trim();
  switch (filter.operator) {
    case 'contains':
      return `Contains "${trimmedValue}"`;
    case 'notContains':
      return `Does not contain "${trimmedValue}"`;
    case 'equals':
      return `Equals "${trimmedValue}"`;
    case 'notEquals':
      return `Not equal to "${trimmedValue}"`;
    case 'startsWith':
      return `Starts with "${trimmedValue}"`;
    case 'endsWith':
      return `Ends with "${trimmedValue}"`;
    case 'isEmpty':
      return 'Is empty';
    case 'isNotEmpty':
      return 'Is not empty';
    default:
      return null;
  }
};

const describeSort = (direction: DescribableSortDirection | null): string | null => {
  if (direction === 'asc') {
    return 'Sorted A to Z';
  }
  if (direction === 'desc') {
    return 'Sorted Z to A';
  }
  return null;
};

export const describeColumnState = (
  sortDirection: DescribableSortDirection | null,
  filter: DescribableColumnFilter | undefined
): string => {
  const parts = [describeSort(sortDirection), describeFilter(filter)].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'All values';
};

export const getDefaultColumnWidth = (column: keyof QuickFillItem): number =>
  column === 'Name' ? NAME_COLUMN_WIDTH : DATA_COLUMN_WIDTH;

export const getMinimumColumnWidth = (column: keyof QuickFillItem): number =>
  column === 'Name' ? MIN_NAME_COLUMN_WIDTH : MIN_DATA_COLUMN_WIDTH;

export const buildGridTemplateColumns = (
  visibleColumns: (keyof QuickFillItem)[],
  columnWidths: Record<string, number>
): string =>
  [
    `${CHECKBOX_COLUMN_WIDTH}px`,
    ...visibleColumns.map((column) => `${columnWidths[column] ?? getDefaultColumnWidth(column)}px`),
  ].join(' ');

export const getTableMinWidth = (
  visibleColumns: (keyof QuickFillItem)[],
  columnWidths: Record<string, number>
): number => (
  CHECKBOX_COLUMN_WIDTH
  + visibleColumns.reduce<number>(
    (total, column) => total + (columnWidths[column] ?? getDefaultColumnWidth(column)),
    0
  )
);

export const slugifyColumnKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const getCellRefKey = (rowIndex: number, columnLabel: string): string =>
  `${rowIndex}::${columnLabel}`;

export const formatRowLabel = (count: number): string =>
  `${count} row${count === 1 ? '' : 's'}`;
